export interface MeterProps {
  /**
   * Consumed share in [0, 1]. Null renders the empty track alone — a bar with
   * no denominator cannot be read, so nothing is filled.
   */
  ratio: number | null;
  /** Fill colour as a token reference, e.g. 'var(--accent-cobre)'. */
  tone: string;
  /** Accessible name of what is metered, already localized. */
  label: string;
  /** Raw figures for the accessible value; omitted when there is no ceiling. */
  used?: number;
  total?: number | null;
  testId?: string;
}

/**
 * A single consumption bar.
 *
 * The one bar in the product: the quota rows and the plan tab's meter cards
 * both draw it from here, so a change to the track height or radius lands
 * everywhere at once.
 *
 * @param props - Component props.
 * @returns The meter.
 */
export function Meter({ ratio, tone, label, used, total, testId }: MeterProps) {
  const filled = ratio === null ? null : Math.min(Math.max(ratio, 0), 1);
  const hasValue = filled !== null && used !== undefined && total !== null && total !== undefined;

  return (
    <div
      role="meter"
      aria-label={label}
      aria-valuemin={hasValue ? 0 : undefined}
      aria-valuenow={hasValue ? used : undefined}
      aria-valuemax={hasValue ? total : undefined}
      data-testid={testId}
      style={{
        height: 'var(--space-1-5)',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--surface-card)',
        overflow: 'hidden',
      }}
    >
      {filled !== null && (
        <div
          style={{
            height: '100%',
            width: `${filled * 100}%`,
            background: tone,
            borderRadius: 'var(--radius-sm)',
          }}
        />
      )}
    </div>
  );
}

export default Meter;
